import { Gamepad2, Clock, Calendar, Archive, Code, CheckCircle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import type { Game } from '../../types/api'
import CategoryTag from '../common/CategoryTag'
import GameOptionsDropdown from '../common/GameOptionsDropdown'

interface ArchivedGameCardProps {
  project: Game
  index: number
  onUnarchive: (project: Game) => void
  onTransfer?: (project: Game) => void
  formatTimeAgo: (dateString: string) => string
}

export const ArchivedGameCard = ({
  project,
  index,
  onUnarchive,
  onTransfer,
  formatTimeAgo
}: ArchivedGameCardProps) => {
  const navigate = useNavigate()
  const projectId = project.id
  
  // Determine if it was completed or in development when archived
  const wasCompleted = project.current_version_id != null
  
  // Get last edit info (for in-development projects)
  const lastEdit = project.last_edit
  const displayComment = lastEdit?.comment || "No recent edits"
  const authorName = lastEdit?.author_name || project.owner_display_name || 'Unknown'
  const authorAvatar = lastEdit?.author_avatar || project.owner_avatar
  
  // Get version comment (for completed projects)
  const versionComment = project.current_version_comment || "No version notes"

  return (
    <div
      className="group relative bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl p-4 hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-200 opacity-60 hover:opacity-100"
    >
      <div className="flex items-start gap-4">
        {/* Game Thumbnail - grayscale for archived */}
        <div className="w-28 h-28 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
          {project.content?.icon_url ? (
            <img 
              src={project.content.icon_url} 
              alt={`${project.name} icon`}
              className="w-full h-full object-cover grayscale"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
                const parent = e.currentTarget.parentElement
                if (parent) {
                  parent.innerHTML = '<svg class="w-12 h-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="6" x2="10" y1="12" y2="12"/><line x1="8" x2="8" y1="10" y2="14"/><line x1="15" x2="15.01" y1="13" y2="13"/><line x1="18" x2="18.01" y1="11" y2="11"/><rect width="20" height="16" x="2" y="4" rx="2"/></svg>'
                }
              }}
            />
          ) : (
            <Gamepad2 className="w-12 h-12 text-gray-400" />
          )}
        </div>

        {/* Game Info */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-2 truncate">
            {project.name}
          </h3>
          
          {/* Tags + Status Badge */}
          <div className="flex gap-2 flex-wrap mb-3">
            {/* Status Badge - In Development or Completed */}
            {wasCompleted ? (
              <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5" />
                Completed
              </span>
            ) : (
              <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 flex items-center gap-1.5">
                <Code className="w-3.5 h-3.5" />
                In Development
              </span>
            )}
            
            {/* Category Tags */}
            {project.content?.tags && project.content.tags.length > 0 ? (
              project.content.tags.slice(0, 2).map((tag, tagIndex) => (
                <CategoryTag key={`tag-${tagIndex}`} categoryId={tag} size="sm" showIcon={true} />
              ))
            ) : project.content?.category ? (
              <CategoryTag categoryId={project.content.category} size="sm" showIcon={true} />
            ) : null}
          </div>
          
          {/* Metadata Row */}
          <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-2">
            <div className="flex items-center gap-1">
              <Archive className="w-4 h-4" />
              <span>Archived {formatTimeAgo(project.updated_at)}</span>
            </div>
            {project.created_at && (
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>Created {formatTimeAgo(project.created_at)}</span>
              </div>
            )}
          </div>
          
          {/* Conditional Bottom Section */}
          {wasCompleted ? (
            /* Completed: Show version and comment */
            <div className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400 italic">
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                v{project.current_bundle_version || project.stats?.versionCount || '1'}
              </span>
              <span className="line-clamp-1">{versionComment}</span>
            </div>
          ) : (
            /* In Development: Show last edit with author */
            <div className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400 italic">
              {/* Author Avatar */}
              {authorAvatar ? (
                <img 
                  src={authorAvatar} 
                  alt={authorName}
                  className="w-5 h-5 rounded-full flex-shrink-0 object-cover"
                />
              ) : (
                <div className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                  {authorName?.charAt(0).toUpperCase() || 'U'}
                </div>
              )}
              <span className="line-clamp-1">{displayComment}</span>
            </div>
          )}
        </div>

        {/* More Actions */}
        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          <GameOptionsDropdown
            project={project}
            onUnarchive={onUnarchive}
            onTransfer={onTransfer}
            stopPropagation={true}
            size="sm"
            buttonClassName="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          />
        </div>
      </div>
    </div>
  )
}

